/**
 * Set or correct one job type's price for one customer (gap 1, DEC-383, D-07/D-08/D-09).
 *
 * The start date is the company's today or later (the API refuses earlier days). A day that
 * already has a change for this job type (today's special price or a scheduled one) is a
 * correction of it: the dialog says so and sends that change's revision as
 * `replacesRevision`, so a change someone else made meanwhile is refused, not overwritten.
 * $0.00 is a price (D-09). There is no way to take a price away (D-07): a change is a new
 * dated row. Approved tickets keep the price they were approved with.
 */

import React from 'react';
import { toast } from 'sonner';
import { rateUnitSuffix } from '@lodex-pro/shared/rate-source';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import type { CustomerContext } from '../../routes/CustomerPricesPage';
import { readJobTypePriceInput, replacesNotice, type JobTypePriceRow } from './customer-prices';
import { useSetJobTypePrice } from './customer-prices-hooks';

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  marginBottom: '4px',
};
const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };
const errorStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-danger)',
  marginTop: '4px',
};

/** The fields an API 400 can name; anything else shows above the buttons. */
const FIELDS = ['familyId', 'rateCents', 'effectiveFrom'] as const;
type Field = (typeof FIELDS)[number];

function isField(path: string | null): path is Field {
  return path !== null && (FIELDS as readonly string[]).includes(path);
}

export interface JobTypePriceDialogProps {
  readonly customer: CustomerContext;
  readonly row: JobTypePriceRow;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function JobTypePriceDialog(props: JobTypePriceDialogProps): React.JSX.Element {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>
        {/* Content unmounts when closed, so every opening starts from a clean form. */}
        <PriceForm
          customer={props.customer}
          row={props.row}
          onDone={(): void => props.onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function PriceForm(props: {
  readonly customer: CustomerContext;
  readonly row: JobTypePriceRow;
  readonly onDone: () => void;
}): React.JSX.Element {
  const { customer, row } = props;
  const [price, setPrice] = React.useState('');
  const [day, setDay] = React.useState(customer.today);
  const [notes, setNotes] = React.useState('');
  const save = useSetJobTypePrice(customer.id);

  // A refusal is about the values sent; editing any field clears it.
  const edit = (set: (v: string) => void, v: string): void => {
    if (save.isError) save.reset();
    set(v);
  };

  const read = readJobTypePriceInput(price);
  const dayOk = day !== '' && day >= customer.today;
  const replaces = dayOk ? replacesNotice(row, day) : null;
  const ok = read.ok && dayOk && !save.isPending;

  const refusal = save.error;
  const fieldError = (f: Field): string | null =>
    refusal !== null && refusal.field === f ? refusal.message : null;
  const otherError = refusal !== null && !isField(refusal.field) ? refusal.message : null;

  const priceError = price !== '' && !read.ok ? read.reason : fieldError('rateCents');
  const dayError =
    day !== '' && day < customer.today
      ? 'Start dates can be today or later.'
      : fieldError('effectiveFrom');

  return (
    <form
      aria-label="Job type price"
      onSubmit={(e): void => {
        e.preventDefault();
        if (!ok || !read.ok) return;
        save.mutate(
          {
            familyId: row.familyId,
            rateCents: read.cents,
            effectiveFrom: day,
            replacesRevision: replaces === null ? null : replaces.revision,
            notes: notes.trim() === '' ? null : notes,
          },
          {
            onSuccess: () => {
              toast.success('Price saved.');
              props.onDone();
            },
          },
        );
      }}
    >
      <DialogHeader>
        <DialogTitle>{`Price for ${row.label} for ${customer.name}`}</DialogTitle>
        <DialogDescription>
          Applies to tickets billed on or after the start date. Approved tickets keep their price.
        </DialogDescription>
      </DialogHeader>
      {fieldError('familyId') !== null && (
        <p role="alert" style={errorStyle}>
          {fieldError('familyId')}
        </p>
      )}
      <div style={{ display: 'grid', gap: '12px', margin: '16px 0' }}>
        <div>
          <label htmlFor="job-type-price" style={labelStyle}>
            {`Price ($${rateUnitSuffix(row.billingMethod)})`}
          </label>
          <Input
            id="job-type-price"
            inputMode="decimal"
            value={price}
            aria-invalid={priceError !== null}
            onChange={(e): void => edit(setPrice, e.target.value)}
          />
          {priceError !== null && (
            <p role="alert" style={errorStyle}>
              {priceError}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="job-type-price-day" style={labelStyle}>
            Start date
          </label>
          <Input
            id="job-type-price-day"
            type="date"
            min={customer.today}
            value={day}
            aria-invalid={dayError !== null}
            onChange={(e): void => edit(setDay, e.target.value)}
          />
          {dayError !== null && (
            <p role="alert" style={errorStyle}>
              {dayError}
            </p>
          )}
          {replaces !== null && (
            <p
              role="status"
              style={{ ...helpStyle, color: 'var(--color-warning)', marginTop: '4px' }}
            >
              {replaces.text}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="job-type-price-notes" style={labelStyle}>
            Notes (optional)
          </label>
          <Input
            id="job-type-price-notes"
            className="keep-case"
            value={notes}
            onChange={(e): void => edit(setNotes, e.target.value)}
          />
        </div>
      </div>
      {otherError !== null && (
        <p role="alert" style={{ ...errorStyle, marginBottom: '8px' }}>
          {otherError}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={props.onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ok}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
